import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { connect, useSelector, useDispatch } from 'react-redux';
import MapWithRegions from 'components/Map/MapOptions/MapWithRegions';
import { PAGE_NAME } from 'utils/constants';
import {
  getTaskMarkers,
  getPersonnelMarkers,
  getEventsMarkers,
  allEventMarkerIcon,
  convertToEmergencyMarkers,
  getTripMarkers,
  convertToEmergencyPolylines,
  convertToTripPolylines,
} from './helper';
import {
  getGridListTransformed,
  getGridRowDetailsTransformed,
} from 'containers/Pages/Tasks/CleanerTasks/Selectors/cleaningTaskSelectors';
import {
  getGridRowDetailsTransformed as selectedWorkerOrderTask,
  getWorkOrderListTransformed,
} from 'containers/Pages/Tasks/WorkOrders/Selectors/workOrderSelectors';
import {
  getPersonnelListRawTransformed,
  getPersonnelDetails,
} from 'containers/Pages/People/Personnel/Selectors/personnelSelectors';
import { detailOpenState, getCurrentPage } from 'reducers/ui';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'utils/constants/index.js';
import { zoneActions, zoneSelectors } from 'containers/Pages/Locations/Zones';
import { updateURLQueryString } from 'sagas/user/help';
import {
  siteSelectors,
  siteDataGridActions,
} from 'containers/Pages/Locations/Sites';
import { eventSelectors, eventsGridActions } from 'containers/Pages/Events';
import { getLocation } from 'utils/DataTransform/Events/eventReportList';
import {
  emergencySelectors,
  emergencyDataGridAction,
} from 'containers/Pages/Tasks/Emergencies';
import { loginStatus } from 'utils/constants/status';
import {
  securitySelectors,
  securityGridTableActions,
} from 'containers/Pages/Tasks/Security';
import { tripSelectors, tripActions } from 'containers/Pages/Tasks/Trips';
import { regionActions, regionSelectors } from 'containers/Pages/Region/Region';
import { actions as cleanerActions } from 'containers/Pages/Tasks/CleanerTasks/Modules/dataGridTable';
import { workOrderGridTableActions } from 'containers/Pages/Tasks/WorkOrders';
import { actions as PersonnelActions } from 'containers/Pages/People/Personnel/Modules/dataGridTable';

export const overlayInitialState = {
  [PAGE_NAME.REGION]: {},
  [PAGE_NAME.CLEANING_TASKS]: {},
  [PAGE_NAME.WORK_ORDER_TASKS]: {},
  [PAGE_NAME.ZONES]: {},
  [PAGE_NAME.SITES]: {},
  [PAGE_NAME.EVENTS]: {},
  [PAGE_NAME.SECURITY]: {},
  [PAGE_NAME.TRANSPORT_OPERATIONAL]: {},
};
// for emergency only
const acceptedPersonnelStatus = [
  loginStatus.AVAILABLE,
  loginStatus.ON_INTERRUPTABLE_TASK,
];

const MapPanel = ({
  personnelList,
  selectedPersonnel,
  cleanerTaskList,
  workOrderTaskList,
  selectedWorkOrderTask,
  selectedCleanerTask,
  requirePanToArea = true,
  detailOpen,
  zoneDetail,
  siteList,
  siteDetails,
  events,
  selectedEvent,
  distresses,
  distress,
  selectedSecurityTask,
  securityTaskList,
  tripList,
  selectedTrip,
  path,
  regionDetail,
  ...props
}) => {
  const [overlays, setOverlays] = useState(overlayInitialState);

  const [mapCenterLatLng, setMapCenterLatLng] = useState(null);
  const pageUrl = useSelector(state => getCurrentPage(state));
  const history = useHistory();
  const dispatch = useDispatch();

  const eventMarkerClicked = marker => {
    setMapCenterLatLng({ lat: marker.lat, lng: marker.lng });
    setOverlays({
      ...overlayInitialState,
      [PAGE_NAME.EVENTS]: { selectedRow: marker.id },
    });
  };

  const openDetailPanel = (pageurl, id) => {
    // depending on which icon on the map is clicked, dispatch the action to add the rowID such that detail panel can be opened
    switch (pageurl) {
      case PAGE_URLS.ZONES.link:
        dispatch(zoneActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.SITES.link:
        dispatch(siteDataGridActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.REGION.link:
        dispatch(regionActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.CLEANING_TASKS.link:
        dispatch(cleanerActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.SECURITY_TASKS.link:
        dispatch(securityGridTableActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.WORK_ORDERS.link:
        dispatch(workOrderGridTableActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.PERSONNEL.link:
        dispatch(PersonnelActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.EVENTS.link:
        dispatch(eventsGridActions.setSelectedRowID(id));
        break;
      case PAGE_URLS.EMERGENCIES.link:
        dispatch(emergencyDataGridAction.setSelectedRowID(id));
        break;
      case PAGE_URLS.TRIPS.link:
        dispatch(tripActions.setSelectedRowID(id));
        break;
      default:
        break;
    }
  };

  const taskMarkerClicked = marker => {
    setMapCenterLatLng({ lat: marker.lat, lng: marker.lng });
  };

  useEffect(() => {
    if (!isEmpty(selectedEvent)) {
      if (selectedEvent.general.id !== overlays[PAGE_NAME.EVENTS].selectedRow) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.EVENTS]: {
            selectedRow: selectedEvent.general.id,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.EVENTS]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent]);

  useEffect(() => {
    if (isEmpty(selectedEvent) && isEmpty(selectedPersonnel)) {
      setMapCenterLatLng(null);
    } else if (!isEmpty(selectedEvent)) {
      setMapCenterLatLng(getLocation(selectedEvent));
      selectedEvent.locations[0]?.ellipse &&
        setMapCenterLatLng({
          lat: selectedEvent.locations[0].ellipse.centre.latitude,
          lng: selectedEvent.locations[0].ellipse.centre.longitude,
        });
    } else if (!isEmpty(selectedPersonnel)) {
      setMapCenterLatLng({
        lat: selectedPersonnel?.operational?.location?.point.latitude,
        lng: selectedPersonnel?.operational?.location?.point.longitude,
      });
    }
  }, [selectedEvent, selectedPersonnel]);

  // WorkOrders
  useEffect(() => {
    if (!isEmpty(selectedWorkOrderTask)) {
      if (
        (selectedWorkOrderTask.others.id.value !==
          overlays[PAGE_NAME.WORK_ORDER_TASKS].selectedRow ||
          selectedWorkOrderTask.others.id.value) &&
        //do not pan to the coords if the task has an image rather than a geographical map
        selectedWorkOrderTask.detailedInfo.site.type === 'map-array'
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.WORK_ORDER_TASKS]: {
            selectedRow: selectedWorkOrderTask.others.id.value,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.WORK_ORDER_TASKS]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkOrderTask]);

  useEffect(() => {
    if (isEmpty(selectedWorkOrderTask)) {
      setMapCenterLatLng(null);
    } else if (
      !isEmpty(selectedWorkOrderTask) &&
      //do not pan to the coords if the task has an image rather than a geographical map
      selectedWorkOrderTask.detailedInfo.site.type === 'map-array'
    ) {
      setMapCenterLatLng({
        lat: selectedWorkOrderTask.others?.surfaceV?.value,
        lng: selectedWorkOrderTask.others?.surfaceU?.value,
      });
    }
  }, [selectedWorkOrderTask]);

  // Cleaning
  useEffect(() => {
    if (!isEmpty(selectedCleanerTask)) {
      if (
        (selectedCleanerTask.others.id.value !==
          overlays[PAGE_NAME.CLEANING_TASKS].selectedRow ||
          selectedCleanerTask.others.id.value) &&
        //do not pan to the coords if the task has an image rather than a geographical map
        selectedCleanerTask.detailedInfo.site.type === 'map-array'
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.CLEANING_TASKS]: {
            selectedRow: selectedCleanerTask.others.id.value,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.CLEANING_TASKS]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCleanerTask]);

  useEffect(() => {
    if (!isEmpty(selectedSecurityTask)) {
      if (
        (selectedSecurityTask.others.id.value !==
          overlays[PAGE_NAME.SECURITY].selectedRow ||
          selectedSecurityTask.others.id.value) &&
        //do not pan to the coords if the task has an image rather than a geographical map
        selectedSecurityTask.detailedInfo.site.type === 'map-array'
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.SECURITY]: {
            selectedRow: selectedSecurityTask.others.id.value,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.SECURITY]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSecurityTask]);

  useEffect(() => {
    if (isEmpty(selectedCleanerTask)) {
      setMapCenterLatLng(null);
    } else if (
      !isEmpty(selectedCleanerTask) &&
      //do not pan to the coords if the task has an image rather than a geographical map
      selectedCleanerTask.detailedInfo.site.type === 'map-array'
    ) {
      setMapCenterLatLng({
        lat: selectedCleanerTask.others?.surfaceV?.value,
        lng: selectedCleanerTask.others?.surfaceU?.value,
      });
    }
  }, [selectedCleanerTask]);

  useEffect(() => {
    if (isEmpty(selectedSecurityTask)) {
      setMapCenterLatLng(null);
    } else if (
      !isEmpty(selectedSecurityTask) &&
      //do not pan to the coords if the task has an image rather than a geographical map
      selectedSecurityTask.detailedInfo.site.type === 'map-array'
    ) {
      setMapCenterLatLng({
        lat: selectedSecurityTask.others?.surfaceV?.value,
        lng: selectedSecurityTask.others?.surfaceU?.value,
      });
    }
  }, [selectedSecurityTask]);

  useEffect(() => {
    if (isEmpty(selectedTrip) && isEmpty(selectedPersonnel)) {
      setMapCenterLatLng(null);
    } else if (!isEmpty(selectedTrip)) {
      setMapCenterLatLng({
        lat: selectedTrip.others.passengerLocation.value.latitude,
        lng: selectedTrip.others.passengerLocation.value.longitude,
      });
    } else if (!isEmpty(selectedPersonnel)) {
      setMapCenterLatLng({
        lat: selectedPersonnel.operational?.location?.point.latitude,
        lng: selectedPersonnel.operational?.location?.point.longitude,
      });
    }
  }, [selectedTrip, selectedPersonnel]);

  useEffect(() => {
    if (!isEmpty(selectedTrip)) {
      if (
        selectedTrip.others.id !==
        overlays[PAGE_NAME.TRANSPORT_OPERATIONAL].selectedRow
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.TRANSPORT_OPERATIONAL]: {
            selectedRow: selectedTrip.others.id,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.TRANSPORT_OPERATIONAL]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrip]);

  // any marker clicked will trigger this to open the detail panel and point to the actual selection in the view
  const handleMarkerClick = ({ id, pageName, position, icon }) => {
    if (
      cleanerTaskList.some(
        item => item.surface && item.surface.icon === icon && !item.surfaceId,
      )
    ) {
      taskMarkerClicked(position);
    } else if (
      workOrderTaskList.some(
        item => item.surface && item.surface.icon === icon && !item.surfaceId,
      )
    ) {
      taskMarkerClicked(position);
    } else if (
      securityTaskList.some(
        item => item.surface && item.surface.icon === icon && !item.surfaceId,
      )
    ) {
      taskMarkerClicked(position);
    } else if (allEventMarkerIcon.includes(icon)) {
      eventMarkerClicked(position);
    }
    // set the id of the marker to highlight the row
    openDetailPanel(pageName, id);
    // go the respective page
    history.push(pageName); // change react route
    updateURLQueryString(pageName); // update the url parameter in redux
  };

  // Bring the layer component here and dynamically build it
  const getMarkers = useCallback(() => {
    const workOrder = getTaskMarkers({
      list: workOrderTaskList,
      selectedTask: selectedWorkOrderTask,
    });

    const cleaningTasks = getTaskMarkers({
      list: cleanerTaskList,
      selectedTask: selectedCleanerTask,
    });

    const eventsMarkers = getEventsMarkers(
      events,
      selectedEvent,
      handleMarkerClick,
    );

    const securityTaskMarkers = getTaskMarkers({
      list: securityTaskList,
      selectedTask: selectedSecurityTask,
    });
    const tripTasks = getTripMarkers(tripList, selectedTrip);
    return [
      ...getPersonnelMarkers(personnelList, selectedPersonnel),
      ...cleaningTasks,
      ...workOrder,
      ...eventsMarkers,
      ...securityTaskMarkers,
      ...tripTasks,
    ];
  }, [
    securityTaskList,
    cleanerTaskList,
    workOrderTaskList,
    personnelList,
    events,
    tripList,
    selectedEvent,
    selectedTrip,
    selectedCleanerTask,
    selectedWorkOrderTask,
    selectedPersonnel,
    selectedSecurityTask,
  ]);

  useEffect(() => {
    if (zoneDetail.others) {
      if (
        zoneDetail.others.id.value !== overlays[PAGE_NAME.ZONES].selectedRow
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.ZONES]: {
            selectedRow: zoneDetail.others.id.value,
          },
        }));
      }
    } else {
      setMapCenterLatLng(null);
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.ZONES]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneDetail]);
  // region
  useEffect(() => {
    if (regionDetail.others) {
      if (
        regionDetail.others.id.value !== overlays[PAGE_NAME.REGION].selectedRow
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.REGION]: {
            selectedRow: regionDetail.others.id.value,
          },
        }));
      }
    } else {
      setMapCenterLatLng(null);
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.REGION]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionDetail]);

  const handlePolygonClick = ({ id, pageName, onClickfitBounds }) => {
    switch (pageName) {
      case PAGE_URLS.ZONES.link: {
        setOverlays({
          ...overlayInitialState,
          [PAGE_NAME.ZONES]: {
            selectedRow: id,
          },
        });
        break;
      }
      case PAGE_URLS.SITES.link: {
        setOverlays({
          ...overlayInitialState,
          [PAGE_NAME.SITES]: {
            selectedRow: id,
          },
        });
        break;
      }
      case PAGE_URLS.REGION.link: {
        setOverlays({
          ...overlayInitialState,
          [PAGE_NAME.REGION]: {
            selectedRow: id,
          },
        });
        break;
      }
      default: {
        return;
      }
    }
    // set the id of the marker to highlight the row
    openDetailPanel(pageName, id);
    // go the respective page
    history.push(pageName); // change react route
    updateURLQueryString(pageName); // update the url parameter in redux
    onClickfitBounds && onClickfitBounds({ zoom: true });
  };
  useEffect(() => {
    if (siteDetails.others) {
      if (
        siteDetails.others.id.value !== overlays[PAGE_NAME.SITES].selectedRow
      ) {
        setOverlays(prevState => ({
          ...prevState,
          [PAGE_NAME.SITES]: {
            selectedRow: siteDetails.others.id.value,
          },
        }));
      }
    } else {
      setOverlays(prevState => ({
        ...prevState,
        [PAGE_NAME.SITES]: {
          selectedRow: null,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteDetails]);

  // for sites
  const getSecondaryPolygonValues = () => {
    return (
      siteList &&
      siteList.length > 0 && {
        secondaryPolygon: siteList.map(item => ({
          ...item.locationDetail[0],
          id: item.id,
          pageName: item.pageName,
          layer: PAGE_NAME.SITES,
          infoWindowData: {
            Name: {
              type: 'text',
              value: item.name,
            },
            ContactNumber: {
              type: 'text',
              value: item.contactNumber,
            },
            ContactName: {
              type: 'text',
              value: item.contactName,
            },
            BusinessClientName: {
              type: 'text',
              value: item.businessClientName,
            },
          },
          onPolygonClick: ({ id, pageName, onClickfitBounds }) =>
            handlePolygonClick({ id, pageName, onClickfitBounds }),
        })),
      }
    );
  };

  const getSecondaryDetail = siteList.find(
    item => item.id === overlays[PAGE_NAME.SITES].selectedRow,
  );
  const isDetailPanelOpen = detailOpen();
  const emergencyMarkers = convertToEmergencyMarkers({
    personnelList,
    distresses,
    distress,
    acceptedPersonnelStatus,
  });

  const emergencyPolylines = convertToEmergencyPolylines(distress);

  if (pageUrl === PAGE_URLS.EMERGENCIES.link) {
    // if distress is true, the show the alternative view
    let centre;
    if (
      distress?.others?.distressee_location?.latitude &&
      distress?.others?.distressee_location?.longitude
    ) {
      centre = {
        lat: distress?.others?.distressee_location?.latitude,
        lng: distress?.others?.distressee_location?.longitude,
      };
    }
    return (
      <MapWithRegions
        detailOpen={isDetailPanelOpen[pageUrl]}
        markers={emergencyMarkers}
        polylines={emergencyPolylines}
        distress={true}
        {...(centre && { center: centre })}
        polygonsInForm
        requirePan
      />
    );
  }

  const totalMarkers = () => {
    if (pageUrl === PAGE_URLS.EMERGENCIES.link) {
      return [...getMarkers(), ...emergencyMarkers];
    } else {
      return getMarkers();
    }
  };
  const eventPolygon = selectedEvent => {
    return selectedEvent?.locations && selectedEvent.locations[0]?.polygon;
  };

  return (
    <MapWithRegions
      requirePan={requirePanToArea}
      distress={false}
      eventPolygon={eventPolygon(selectedEvent)} // helps to pan to event polygon
      // displaySites={true}
      markers={totalMarkers()}
      markerClick={handleMarkerClick}
      center={mapCenterLatLng}
      detailOpen={isDetailPanelOpen[pageUrl]}
      polylines={path}
      selectedZone={overlays[PAGE_NAME.ZONES].selectedRow}
      selectedRegion={overlays[PAGE_NAME.REGION].selectedRow}
      selectedSecondaryPolygon={overlays[PAGE_NAME.SITES].selectedRow}
      onZoneClick={({ id, pageName, onClickfitBounds }) =>
        handlePolygonClick({ id, pageName, onClickfitBounds })
      }
      polygonsInForm={false} // this tells the polygons like sites and zones to disable the click and not showing the popup
      secondaryDetail={getSecondaryDetail}
      // setComponentMap={setComponentMap}
      {...getSecondaryPolygonValues()}
      {...props}
    />
  );
};

MapPanel.propTypes = {
  personnelList: PropTypes.array,
  selectedPersonnel: PropTypes.object,
  zoneGridAPI: PropTypes.object,
  cleanerTaskList: PropTypes.array,
  selectedCleanerTask: PropTypes.object,
  requirePanToArea: PropTypes.object,
  detailOpen: PropTypes.func,
  distresses: PropTypes.array,
  workOrderTaskList: PropTypes.array,
  securityTaskList: PropTypes.array,
  tripList: PropTypes.array,
  siteList: PropTypes.array,
  events: PropTypes.array,
  selectedWorkOrderTask: PropTypes.object,
  selectedEvent: PropTypes.object,
  siteDetails: PropTypes.object,
  zoneDetail: PropTypes.object,
  distress: PropTypes.object,
  selectedSecurityTask: PropTypes.object,
  regionDetail: PropTypes.object,
  selectedTrip: PropTypes.object,
  path: PropTypes.object,
};

const mapStateToProps = state => ({
  personnelList: getPersonnelListRawTransformed(state),
  selectedPersonnel: getPersonnelDetails(state),
  events: eventSelectors.getEventListRaw(state),
  selectedEvent: eventSelectors.getEventDetailsRaw(state),
  siteList: siteSelectors.getSiteListTransformed(state),
  siteDetails: siteSelectors.getSiteDetailsTransformed(state),
  selectedCleanerTask: getGridRowDetailsTransformed(state),
  selectedSecurityTask: securitySelectors.getGridRowDetailsTransformed(state),
  selectedWorkOrderTask: selectedWorkerOrderTask(state),
  cleanerTaskList: getGridListTransformed(state),
  securityTaskList: securitySelectors.getSecurityListTransformed(state),
  workOrderTaskList: getWorkOrderListTransformed(state),
  zoneDetail: zoneSelectors.getZoneDetailsTransformed(state),
  regionDetail: regionSelectors.getTransformedRegionDetails(state),
  detailOpen: () => detailOpenState(state),
  distresses: emergencySelectors.getEmergencyListTransformed(state),
  distress: emergencySelectors.getEmergencyDetailTransformed(state),
  path: tripSelectors.getTripDetailsTransformed(state).others
    ? convertToTripPolylines(
        tripSelectors.getTripDetailsTransformed(state).others,
      )
    : {
        markers: [],
      },
  tripList: tripSelectors.getTripListTransformed(state),
  selectedTrip: tripSelectors.getTripDetailsTransformed(state),
});

export default connect(mapStateToProps)(MapPanel);
